import { Injectable } from '@angular/core';

export interface SelfServiceConfig {
  name: string;
  remoteUrl: string;
  rootElementId?: string;
  rootElementTag?: string;
  scriptFiles: string[];
  styleFiles: string[];
}

@Injectable({
  providedIn: 'root',
})
export class SelfServiceLoaderService {
  private loadedScripts = new Set<string>();
  private loadedStyles = new Set<string>();

  /**
   * Load a self-service app from remote URL
   * @param config Self-service configuration
   */
  async loadSelfService(config: SelfServiceConfig, hostElement: HTMLElement): Promise<void> {
    try {
      this.prepareScriptMountElement(config, hostElement);

      // Load styles first
      await this.loadStylesheets(config.styleFiles);

      // Load scripts in order
      for (const scriptUrl of config.scriptFiles) {
        await this.loadScript(scriptUrl);
      }

      console.log(`Self-service app "${config.name}" loaded successfully`);
    } catch (error) {
      console.error(`Failed to load self-service app "${config.name}":`, error);
      throw error;
    }
  }

  private prepareScriptMountElement(config: SelfServiceConfig, hostElement: HTMLElement): HTMLElement {
    hostElement.innerHTML = '';

    const mountElement = document.createElement(config.rootElementTag ?? 'div');
    mountElement.id = config.rootElementId ?? 'selfservice-app-root';
    mountElement.className = 'selfservice-app-root';
    hostElement.appendChild(mountElement);

    return mountElement;
  }

  /**
   * Load a single stylesheet
   */
  private loadStylesheet(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.loadedStyles.has(url)) {
        resolve();
        return;
      }

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;

      link.onload = () => {
        this.loadedStyles.add(url);
        resolve();
      };

      link.onerror = () => {
        reject(new Error(`Failed to load stylesheet: ${url}`));
      };

      document.head.appendChild(link);
    });
  }

  /**
   * Load multiple stylesheets
   */
  private async loadStylesheets(urls: string[]): Promise<void> {
    const promises = urls.map(url => this.loadStylesheet(url));
    await Promise.all(promises);
  }

  /**
   * Load a single script dynamically
   */
  private loadScript(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.loadedScripts.has(url)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = url;
      script.type = 'text/javascript';
      script.async = true;

      script.onload = () => {
        this.loadedScripts.add(url);
        resolve();
      };

      script.onerror = () => {
        reject(new Error(`Failed to load script: ${url}`));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Unload a self-service app and cleanup
   */
  unloadSelfService(config: SelfServiceConfig): void {
    // Remove scripts from tracking
    config.scriptFiles.forEach(url => this.loadedScripts.delete(url));

    // Remove styles
    config.styleFiles.forEach(url => {
      const link = document.querySelector(`link[href="${url}"]`) as HTMLLinkElement;
      if (link) {
        link.remove();
        this.loadedStyles.delete(url);
      }
    });

    const appRoot = document.getElementById(config.rootElementId ?? 'selfservice-app-root');
    appRoot?.remove();
  }
}
